/**
 * @file ReelConfig.ts
 * @role Loads + validates reels.config.json: the grid dimensions (M5) and the spin timing
 *       + stop-planner tuning (M6/M8). All spin "feel" lives here, never inline in code —
 *       changing spin timing is a config edit (see TASK_RECIPES "Change spin timing").
 * @owns The validated grid + spin + stopPlanner values.
 * @reads Bundled reels.config.json (default) or injected raw config (tests).
 * @neverDoes Render. Decide outcomes. Drive motion (the engine does, on these numbers).
 * @invariants 10 (validate before use).
 * @see docs/REEL_SYSTEM.md, docs/CONFIG_GUIDE.md
 */
import { z } from 'zod';
import reelsConfigRaw from '@config/reels.config.json';
import { GameError } from '@core/errors/GameError';

const positiveInt = z.number().int().positive();
const nonNegInt = z.number().int().nonnegative();

const ReelsConfigSchema = z.object({
  grid: z.object({ columns: positiveInt, rows: positiveInt }),
  spin: z.object({
    minSpinMs: nonNegInt,
    accelerationMs: positiveInt,
    reelStopDelayMs: nonNegInt,
    decelerationMs: positiveInt,
    bounceMs: nonNegInt,
    direction: z.enum(['up', 'down']).default('down'),
    turboMinSpinMs: nonNegInt,
    turboReelStopDelayMs: nonNegInt,
    symbolSpeedPxPerSecond: positiveInt,
  }),
  stopPlanner: z.object({
    minLeadInSymbols: positiveInt,
    turboMinLeadInSymbols: positiveInt,
  }),
  spinTimingPolicy: z.object({
    minDecelerationMs: positiveInt,
    turboDecelerationMultiplier: z.number().positive(),
    errorDecelerationMultiplier: z.number().positive(),
  }),
});

export type SpinConfig = z.infer<typeof ReelsConfigSchema>['spin'];
export type StopPlannerConfig = z.infer<typeof ReelsConfigSchema>['stopPlanner'];
export type SpinTimingPolicyConfig = z.infer<typeof ReelsConfigSchema>['spinTimingPolicy'];

export class ReelConfig {
  readonly columns: number;
  readonly rows: number;
  readonly spin: SpinConfig;
  readonly stopPlanner: StopPlannerConfig;
  readonly spinTimingPolicy: SpinTimingPolicyConfig;

  constructor(raw: unknown = reelsConfigRaw) {
    const parsed = ReelsConfigSchema.safeParse(raw);
    if (!parsed.success) {
      throw new GameError('CONFIG_LOAD_FAILED', `Invalid reels config: ${parsed.error.message}`, {
        cause: parsed.error,
      });
    }
    this.columns = parsed.data.grid.columns;
    this.rows = parsed.data.grid.rows;
    this.spin = parsed.data.spin;
    this.stopPlanner = parsed.data.stopPlanner;
    this.spinTimingPolicy = parsed.data.spinTimingPolicy;
  }
}
